import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const HERO = "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.9 2.4 30.3 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.1C12.2 13.6 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.1 5.3-4.6 6.9l7.1 5.5c4.2-3.9 6.6-9.6 6.6-16.4z" />
      <path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.5-5.7l-7.1-5.5c-2 1.4-4.6 2.2-8.4 2.2-6.4 0-11.8-4.1-13.6-9.9l-7.9 6.1C6.4 42.6 14.6 48 24 48z" />
    </svg>
  );
}

function GoogleButton({ label = "Continue with Google", testid }) {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" onClick={handleGoogle} variant="outline" data-testid={testid}
      className="w-full rounded-full border-stone-300 bg-white hover:bg-muted h-11 font-medium">
      <GoogleIcon /> <span className="ml-2">{label}</span>
    </Button>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-ink text-white">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <Link to="/" className="font-serif font-bold text-2xl tracking-tight relative z-10">
          blinxd<span className="text-terracotta">.</span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-serif font-bold text-5xl tracking-tight leading-[1.05]">
            Stop buying backlinks.<br /><span className="italic text-terracotta">Swap them.</span>
          </h2>
          <p className="text-stone-300 mt-6 max-w-sm leading-relaxed">
            DA-matched, niche-locked backlink exchange with AI-written guest posts.
          </p>
        </div>
        <div className="relative z-10 font-mono text-[11px] tracking-wide text-stone-400">
          FAIR EXCHANGE GUARANTEE · AI BRIDGE STUDIO
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="lg:hidden font-serif font-bold text-2xl tracking-tight block mb-6">
            blinxd<span className="text-terracotta">.</span>
          </Link>
          <h1 className="font-serif font-bold text-4xl tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2 text-sm">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function ErrorBox({ children }) {
  return (
    <div data-testid="auth-error" className="rounded-xl border border-terracotta/30 bg-terracotta-light/30 text-terracotta-dark text-sm px-4 py-2.5">
      {children}
    </div>
  );
}

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      const dest = !data.plan_chosen ? "/pricing" : data.site?.owner_verified ? "/feed" : "/dashboard";
      nav(dest);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your blinxd pool.">
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email" className="rounded-xl bg-white border-stone-300 h-11" placeholder="you@site.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password" className="rounded-xl bg-white border-stone-300 h-11" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full rounded-full bg-terracotta text-white hover:bg-terracotta-dark h-11 active:scale-95 transition-transform">
          {busy ? "Logging in…" : "Log in"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-muted-foreground">
        <div className="h-px flex-1 bg-border" /><span className="font-mono text-[10px] tracking-widest">OR</span><div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton testid="google-login-button" />
      <p className="mt-6 text-sm text-muted-foreground text-center">
        No account? <Link to="/register" className="text-terracotta font-medium hover:underline" data-testid="to-register">Sign up</Link>
      </p>
    </AuthShell>
  );
}
